import { useEffect } from "react";
import { Link } from "react-router-dom";
import PageHero from "@/components/PageHero";

export default function Terms() {
    useEffect(() => {
        document.title = "Surveys2CAD | Terms of Service";
    }, []);

    const lastUpdated = "February 18, 2026";

    return (
        <div data-testid="terms-page">
            <PageHero variant="about" tint="orange">
                <div className="max-w-4xl mx-auto px-5 lg:px-8 py-16 md:py-20">
                    <div className="overline mb-3 !text-orange-400">Legal</div>
                    <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-50 tracking-tight">
                        Terms of Service
                    </h1>
                    <p className="text-slate-400 mt-4 text-sm font-mono-alt">Last updated: {lastUpdated}</p>
                </div>
            </PageHero>

            <section className="max-w-4xl mx-auto px-5 lg:px-8 py-16 md:py-20">
                <div className="prose prose-invert prose-slate max-w-none space-y-6 text-slate-300 leading-relaxed text-[15px]">
                    <p>
                        These Terms of Service ("<b>Terms</b>") govern your access to and use of the website at
                        <b> surveys2cad.com</b> and any related drafting services (collectively, the "<b>Services</b>")
                        provided by Surveys2CAD ("<b>we</b>", "<b>us</b>", "<b>our</b>"). By using the Services,
                        you agree to be bound by these Terms.
                    </p>

                    <h2 className="font-heading text-2xl text-slate-50 pt-4">1. Services</h2>
                    <p>
                        We provide land surveying CAD drafting — including but not limited to boundary, topographic,
                        ALTA/NSPS, as-built, subdivision plat, construction staking, route/corridor, FEMA elevation
                        certificates, site plans, volumetric/earthwork, wetland delineation, and 3D laser scanning deliverables.
                        The exact scope, file format, turnaround, and fee are documented in the project acceptance email or
                        signed statement of work.
                    </p>

                    <h2 className="font-heading text-2xl text-slate-50 pt-4">2. Quotes &amp; Acceptance</h2>
                    <ul className="list-disc list-inside space-y-1.5">
                        <li>Quotes are non-binding estimates valid for 30 days.</li>
                        <li>A project is considered "accepted" when you reply in writing confirming the fee and scope.</li>
                        <li>We begin production only after written acceptance — or, for rush jobs, after a 50% deposit.</li>
                    </ul>

                    <h2 className="font-heading text-2xl text-slate-50 pt-4">3. Payment</h2>
                    <ul className="list-disc list-inside space-y-1.5">
                        <li>Invoices are <b>Net 15</b> unless otherwise agreed.</li>
                        <li>Accepted methods: ACH, wire, or major credit cards.</li>
                        <li>Late payments accrue 1.5% interest per month (or the maximum permitted by law, whichever is lower).</li>
                        <li>Project files remain our property until the invoice is paid in full.</li>
                    </ul>

                    <h2 className="font-heading text-2xl text-slate-50 pt-4">4. Revisions</h2>
                    <p>
                        Every project includes <b>two rounds</b> of revisions at no extra charge, provided the request is within
                        the original scope. Scope changes (additional sheets, new deliverables, re-drafting to new standards)
                        are quoted separately before work begins.
                    </p>

                    <h2 className="font-heading text-2xl text-slate-50 pt-4">5. Client Responsibilities</h2>
                    <ul className="list-disc list-inside space-y-1.5">
                        <li>Provide accurate source data — field notes, RAW files, point clouds, record research.</li>
                        <li>Promptly review deliverables; approvals are deemed final if no response within 10 business days.</li>
                        <li>Warrant that you have the right to share all uploaded data.</li>
                        <li>Obtain any surveyor-of-record stamp from your licensed surveyor — we do not certify drawings.</li>
                    </ul>

                    <h2 className="font-heading text-2xl text-slate-50 pt-4">6. Intellectual Property</h2>
                    <p>
                        On full payment, the final deliverables become your property under a royalty-free, perpetual license to
                        use them for the intended project. We retain the right to showcase anonymized portfolio excerpts
                        (no client name, no location, no PII) unless you opt out in writing. Any proprietary drafting layers,
                        scripts, or templates we develop remain our intellectual property.
                    </p>

                    <h2 className="font-heading text-2xl text-slate-50 pt-4">7. Confidentiality</h2>
                    <p>
                        All project files, specifications, and deliverables are treated as confidential. Our staff and
                        sub-contractors sign NDAs covering your data. We will not disclose project details to any third party
                        without your written consent, except when required by law.
                    </p>

                    <h2 className="font-heading text-2xl text-slate-50 pt-4">8. Disclaimers &amp; Limitation of Liability</h2>
                    <p>
                        The Services are provided "<b>as is</b>". We draft based on the data you provide — we do not field-verify,
                        re-survey, or certify measurements, and we do not act as a licensed surveyor of record unless a
                        separately stamped agreement says otherwise. To the maximum extent permitted by law, our aggregate
                        liability arising out of or relating to these Terms is limited to the fees paid by you for the specific
                        project giving rise to the claim. We are not liable for indirect, consequential, special, incidental,
                        or punitive damages.
                    </p>

                    <h2 className="font-heading text-2xl text-slate-50 pt-4">9. Indemnification</h2>
                    <p>
                        You agree to indemnify and hold us harmless from any claims, damages, or expenses arising out of
                        (a) your breach of these Terms, (b) incorrect or unlawful source data you provided, or (c) your use of
                        the deliverables beyond their intended scope.
                    </p>

                    <h2 className="font-heading text-2xl text-slate-50 pt-4">10. Termination</h2>
                    <p>
                        Either party may terminate a project with 7 days' written notice. You are responsible for work completed
                        up to the termination date, pro-rated. Unpaid invoices become immediately due upon termination.
                    </p>

                    <h2 className="font-heading text-2xl text-slate-50 pt-4">11. Governing Law</h2>
                    <p>
                        These Terms are governed by the laws of the State of Pennsylvania, USA. Any dispute not resolved
                        informally within 30 days shall be submitted to binding arbitration under the rules of the American
                        Arbitration Association, held in Pittsburgh, PA.
                    </p>

                    <h2 className="font-heading text-2xl text-slate-50 pt-4">12. Changes to These Terms</h2>
                    <p>
                        We may update these Terms from time to time. The "Last updated" date reflects the current version. Continued
                        use of the Services after changes are posted constitutes acceptance.
                    </p>

                    <h2 className="font-heading text-2xl text-slate-50 pt-4">13. Contact</h2>
                    <p>
                        Questions about these Terms? Email <a className="text-cyan-300 hover:text-cyan-200" href="mailto:surveys2cad@gmail.com">surveys2cad@gmail.com</a>
                        {" "}or visit our <Link to="/contact" className="text-cyan-300 hover:text-cyan-200">contact page</Link>.
                    </p>

                    <p className="text-xs text-slate-500 pt-6 border-t border-slate-800 mt-10">
                        See also: <Link to="/privacy" className="text-cyan-400 hover:text-cyan-300">Privacy Policy</Link>.
                    </p>
                </div>
            </section>
        </div>
    );
}
